import { z } from "zod"

/* -------------------------------------------------------------------------------------------------
 * errors
 * -----------------------------------------------------------------------------------------------*/
enum ApiFailure {
  FETCH_ERROR = "FETCH_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  RESPONSE_PARSING_ERROR = "RESPONSE_PARSING_ERROR",
  INVALID_ERROR_RESPONSE_ERROR = "INVALID_ERROR_RESPONSE_ERROR",
  INVALID_SUCCESS_RESPONSE_ERROR = "INVALID_SUCCESS_RESPONSE_ERROR",
  UNPROCESSABLE_ERROR = "UNPROCESSABLE_ERROR",
  REDIRECT_ERROR = "REDIRECT_ERROR",
}

class UnhandledApiError<T> extends Error {
  status: ApiFailure
  originalStatus?: number

  data?: T
  meta?: { request: Request; response?: Response }

  errorRedirect: string

  override stack?: string

  constructor({
    status,
    originalStatus,
    data,
    meta,
    stack = "",
    message,
    errorRedirect,
  }: {
    status: ApiFailure
    originalStatus?: number
    data?: T
    meta?: { request: Request; response?: Response }
    stack?: string
    message: string
    errorRedirect?: string
  }) {
    super(message)
    this.status = status
    this.meta = meta
    this.originalStatus = originalStatus
    this.data = data
    this.errorRedirect = `${errorRedirect || "/errore"}${window.location.search}`
    if (stack) {
      this.stack = stack
    } else {
      Error.captureStackTrace(this, this.constructor)
    }
  }
}

class HandledApiError<T, E extends string = string> extends Error {
  data?: T
  type: E

  override stack?: string

  constructor({
    data,
    stack = "",
    message = "A handled api error was thrown",
    type = "" as E,
  }: {
    data?: T
    stack?: string
    message?: string
    type?: E
  }) {
    super(message)
    this.data = data
    this.type = type
    if (stack) {
      this.stack = stack
    } else {
      Error.captureStackTrace(this, this.constructor)
    }
  }
}

/* -------------------------------------------------------------------------------------------------
 * schemas
 * -----------------------------------------------------------------------------------------------*/

const baseApiResponseSchema = z.object({
  status: z.string(),
  channel: z.number().nullish().optional(),
  requestId: z.string().nullish().optional(),
  datetime: z.string().nullish().optional(),
})

type BaseApiResponse = z.infer<typeof baseApiResponseSchema>

const errorInfosSchema = z.record(z.string(), z.array(z.string())).optional()

const baseApiErrorResponseSchema = baseApiResponseSchema.extend({
  error: z
    .object({
      type: z.string(),
      message: z.string().optional(),
      errorInfos: errorInfosSchema,
    })
    .optional(),
})

type BaseApiErrorResponse = z.infer<typeof baseApiErrorResponseSchema>

export {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  errorInfosSchema,
  HandledApiError,
  UnhandledApiError,
}
export type { BaseApiErrorResponse, BaseApiResponse }
